import React, { useContext, useReducer, createContext, ReactNode } from 'react';
import { IGiftCardCommonData } from '../models/common-models';

interface IListState {
  transactableGiftCardList: IGiftCardCommonData[];
  selectedGiftCardPaymentIDs?: string[];
  isViewBarcode?: boolean;
}

export const LIST_ACTIONS = {
  SET_LIST: 'SET_LIST',
  SELECT_CARD: 'SELECT_CARD',
  VIEW_BARCODE: 'VIEW_BARCODE',
  UPDATE_CARD_BARCODE: 'UPDATE_CARD_BARCODE',
  CLEAR_STATE: 'CLEAR_STATE'
};

type ListAction =
  | { type: LIST_ACTIONS.SET_LIST; payload: IGiftCardCommonData[] }
  | { type: LIST_ACTIONS.SELECT_CARD; payload: string[] }
  | { type: LIST_ACTIONS.VIEW_BARCODE; payload: boolean }
  | { type: LIST_ACTIONS.UPDATE_CARD_BARCODE; payload: Partial<IGiftCardCommonData>[] }
  | { type: LIST_ACTIONS.CLEAR_STATE };

const initialState: IListState = {
  transactableGiftCardList: [],
  selectedGiftCardPaymentIDs: [],
  isViewBarcode: false
};

const ListContext = createContext<{
  state: IListState;
  dispatch: React.Dispatch<ListAction>;
}>({
  state: initialState,
  dispatch: () => null
});

const listReducer = (state: IListState, action: ListAction): IListState => {
  switch (action.type) {
  case LIST_ACTIONS.SET_LIST:
    return {
      ...state,
      transactableGiftCardList: Array.isArray(action.payload) ? action.payload : []
    };
  case LIST_ACTIONS.SELECT_CARD:
    return {
      ...state,
      selectedGiftCardPaymentIDs: action.payload
    };
  case LIST_ACTIONS.VIEW_BARCODE:
    return {
      ...state,
      isViewBarcode: action.payload
    };
  case LIST_ACTIONS.UPDATE_CARD_BARCODE: {
    const newList = [...state.transactableGiftCardList];
    action.payload.forEach((card) => {
      const index = newList.findIndex((_card) => _card.id === card.id);
      if (index !== -1) {
        newList[index] = { ...newList[index], ...card };
      }
    });
    return {
      ...state,
      transactableGiftCardList: newList
    };
  }
  case LIST_ACTIONS.CLEAR_STATE:
    return initialState;
  default:
    return state;
  }
};

interface IListProviderProps {
  children: ReactNode;
}

export const ListProvider: React.FC<IListProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(listReducer, initialState);
  return <ListContext.Provider value={{ state, dispatch }}>{children}</ListContext.Provider>;
};

export const useListContext = () => {
  const context = useContext(ListContext);
  if (!context) {
    throw new Error('useListContext must be used within a ListProvider');
  }
  return context;
};
